import { useEffect, useRef, useState } from "react";
import {
  AGENT_MEDIA_ACCEPT,
  AGENT_MEDIA_LIMITS,
  type DocumentEntry,
  type UpdateDocumentInput,
} from "@school-sales-ai/shared";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useSaveDocumentMutation } from "@/hooks/useDocuments";
import {
  AGENT_MEDIA_LIMITS_HINT,
  checkAgentMediaFile,
  formatFileSize,
} from "@/lib/agent-media-file";

// Mirror the shared Zod schema (promptSafeText(120) / promptSafeText(500)).
const TITLE_MAX = 120;
const DESCRIPTION_MAX = 500;

type EditDocumentModalProps = {
  doc: DocumentEntry | null;
  onClose: () => void;
};

export function EditDocumentModal({
  doc,
  onClose,
}: EditDocumentModalProps): React.JSX.Element {
  const saveMutation = useSaveDocumentMutation();

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Pre-populate when a document is opened for editing.
  useEffect(() => {
    if (doc) {
      setTitle(doc.title);
      setDescription(doc.description);
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }, [doc?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const { error: fileError } = checkAgentMediaFile(file);

  const canSubmit =
    title.trim().length > 0 &&
    title.length <= TITLE_MAX &&
    description.trim().length > 0 &&
    description.length <= DESCRIPTION_MAX &&
    fileError === null &&
    !saveMutation.isPending;

  // Only the fields that actually changed, or null. Every write moves the row's
  // `updatedAt`, which lets the agent re-send the material to a prospect who
  // already has it — a save that changed nothing must not do that.
  function changedMetadata(current: DocumentEntry): UpdateDocumentInput | null {
    const next = {
      title: title.trim(),
      description: description.trim(),
    };
    const changed = next.title !== current.title || next.description !== current.description;
    return changed ? next : null;
  }

  function handleOpenChange(open: boolean): void {
    if (!open) onClose();
  }

  function handleSubmit(e: React.FormEvent): void {
    e.preventDefault();
    if (!doc || !canSubmit) return;
    const data = changedMetadata(doc);
    if (data === null && file === null) {
      onClose();
      return;
    }
    saveMutation.mutate({ id: doc.id, data, file }, { onSuccess: () => onClose() });
  }

  return (
    <Dialog open={doc !== null} onOpenChange={handleOpenChange}>
      <DialogContent className="flex flex-col max-h-[90vh]">
        <DialogHeader>
          <DialogTitle>Editar material</DialogTitle>
        </DialogHeader>

        <form
          id="edit-document-form"
          onSubmit={handleSubmit}
          className="flex flex-col gap-3 overflow-y-auto pr-1"
        >
          <div className="flex flex-col gap-1">
            <label
              htmlFor="edit-doc-title"
              className="text-xs font-medium text-ink-600"
            >
              Título
            </label>
            <input
              id="edit-doc-title"
              type="text"
              value={title}
              maxLength={TITLE_MAX}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Reglamento escolar"
              className="rounded-[10px] border border-ink-200 px-3 py-2 text-sm text-ink-800 focus:border-moss focus:outline-none"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label
              htmlFor="edit-doc-description"
              className="text-xs font-medium text-ink-600"
            >
              Descripción (el agente la usa para decidir cuándo compartirlo)
            </label>
            <textarea
              id="edit-doc-description"
              value={description}
              maxLength={DESCRIPTION_MAX}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              placeholder="Reglas de convivencia, uniformes y horarios de atención…"
              className="resize-none rounded-[10px] border border-ink-200 px-3 py-2 text-sm text-ink-800 focus:border-moss focus:outline-none"
            />
          </div>

          {doc && (
            <div className="flex flex-col gap-1">
              <label htmlFor="edit-doc-file" className="text-xs font-medium text-ink-600">
                Reemplazar archivo (opcional)
              </label>
              <p className="text-[11px] text-ink-400">
                Actual: {AGENT_MEDIA_LIMITS[doc.kind].icon} {AGENT_MEDIA_LIMITS[doc.kind].label} ·{" "}
                {doc.fileName} · {formatFileSize(doc.fileSizeBytes)}
              </p>
              <input
                id="edit-doc-file"
                ref={fileInputRef}
                type="file"
                accept={AGENT_MEDIA_ACCEPT}
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                className="text-sm text-ink-700 file:mr-3 file:rounded-lg file:border-0 file:bg-ink-100 file:px-3 file:py-1.5 file:text-xs file:font-semibold file:text-ink-700 hover:file:bg-ink-200"
              />
              <p className="text-[11px] text-ink-400">
                {AGENT_MEDIA_LIMITS_HINT}. El título, la descripción y el producto se conservan.
              </p>
              {fileError && <p className="text-xs text-red-500">{fileError}</p>}
            </div>
          )}
        </form>

        <DialogFooter className="mt-2">
          <Button type="submit" form="edit-document-form" disabled={!canSubmit}>
            {saveMutation.isPending ? "Guardando…" : "Guardar cambios"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
